class PredictiveSearch extends SearchForm {
  constructor() {
    super();

    this.ACTIVE_CLASS = "predictive-search--active";

    this.cachedResults = {};
    this.abortController = new AbortController();
    this.searchTerm = "";
  }

  /**
   * Lifecycle method called when the element is added to the DOM.
   */
  connectedCallback() {
    super.connectedCallback();

    this.addEventListeners();

    this.predictiveSearch = this.querySelector("[data-predictive-search]");
    this.predictiveSearchWrapper = this.querySelector(
      "[data-predictive-search-wrapper]",
    );
    this.statusElement = this.querySelector("[data-predictive-search-status]");

    this.renderRecentlyViewed();
  }

  /**
   * Adds event listeners for form submit, input focus, and focus out.
   */
  addEventListeners() {
    this.form?.addEventListener("submit", this.onFormSubmit.bind(this));
    this.input?.addEventListener("click", () => this.setSearchState("add"));
    this.input?.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("click", this.onTabClick.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));

    document.addEventListener("click", (event) => {
      const isToggle = event.target.closest("[data-search-toggle]");
      const isActive = this.classList.contains(this.IS_ACTIVE_CLASS);
      if (!isActive) return;
      if (!this.contains(event.target) && !isToggle)
        this.searchExit(new Event("click"));
    });
    document.addEventListener("keydown", (event) => {
      if (event.key !== "Escape") return;
      const isActive = this.classList.contains(this.IS_ACTIVE_CLASS);
      if (isActive) this.searchExit(new Event("click"));
    });

    subscribe(PUB_SUB_EVENTS.drawerClosed, () => this.closeResults(true));
  }

  /**
   * Handles input changes, updates search term, and fetches results.
   */
  onChange() {
    super.onChange();

    this.searchTerm = this.query;

    if (!this.searchTerm.length) {
      if (this.predictiveSearchWrapper)
        this.predictiveSearchWrapper.innerHTML = "";
      return this.close();
    }

    this.getSearchResults(this.searchTerm);
  }

  /**
   * Handles form submission, prevents submit if no query or a result is selected.
   * @param {Event} event
   */
  onFormSubmit(event) {
    if (!this.query.length) event.preventDefault();
  }

  /**
   * Handles input focus, triggers search if needed.
   */
  onFocus() {
    const currentSearchTerm = this.query;

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) this.onChange();

    this.open();
  }

  /**
   * Handles keyup events for navigation and selection.
   * @param event
   */
  onKeyup(event) {
    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  /**
   * Handles focus out, closes results if focus leaves the component.
   */
  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  /**
   * Fetches search results and renders them, using cache if available.
   * @param {string} searchTerm
   */
  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();

    if (this.cachedResults[queryKey]) {
      return this.renderSearchResults(this.cachedResults[queryKey]);
    }

    this.fetchSearchResults(searchTerm, queryKey);
  }

  /**
   * Fetches search results from the server.
   * @param searchTerm
   * @param queryKey
   */
  fetchSearchResults(searchTerm, queryKey) {
    const baseUrl = routes.predictive_search_url;
    const encodedTerm = encodeURIComponent(searchTerm);
    const parameters =
      "&resources[options][fields]=title,body,product_type,variants.title,vendor,variants.sku";
    const url = `${baseUrl}?q=${encodedTerm}${parameters}&section_id=predictive-search`;

    this.open();

    fetch(url, { signal: this.abortController.signal })
      .then((response) => this.handleResponse(response))
      .then((text) => this.processFetchedResults(text, queryKey))
      .catch((error) => this.handleError(error));
  }

  /**
   * Handles the fetch response, throwing an error for non-OK responses.
   * @param {Object} response
   * @returns Promise<string>
   */
  handleResponse(response) {
    if (!response.ok) this.handleResponseError(response);

    return response.text();
  }

  /**
   * Handles non-OK fetch responses by closing results and throwing an error.
   * @param {Object} response
   */
  handleResponseError(response) {
    this.close();

    throw new Error(response.status);
  }

  /**
   * Handles fetch errors, ignoring abort errors and closing results on others.
   * @param {Error} error
   */
  handleError(error) {
    if (error?.code === 20) return;

    this.close();

    throw error;
  }

  /**
   * Processes fetched results, caches them, and renders.
   * @param text
   * @param queryKey
   */
  processFetchedResults(text, queryKey) {
    const parsedText = new DOMParser().parseFromString(text, "text/html");
    const resultsElement = parsedText.querySelector(
      "#shopify-section-predictive-search",
    );
    const resultsMarkup = resultsElement.innerHTML;

    this.cachedResults[queryKey] = resultsMarkup;
    this.renderSearchResults(resultsMarkup);
  }

  /**
   * Renders the search results markup and updates live region.
   * @param {string} resultsMarkup
   */
  renderSearchResults(resultsMarkup) {
    if (this.predictiveSearchWrapper) {
      this.predictiveSearchWrapper.innerHTML = resultsMarkup;

      this.initTabs();
      this.setLiveRegionText();
      this.open();
    }
  }

  /**
   * Initializes tabs by activating the first tab and its corresponding results panel.
   */
  initTabs() {
    const tabs = this.querySelectorAll("[data-tab]");
    const results = this.querySelectorAll("[data-results-type]");

    if (!tabs.length || !results.length) return;

    const firstTab = tabs[0];
    const activeType = firstTab.dataset.tab;

    tabs.forEach((t) =>
      t.classList.toggle("predictive-search__tab--active", t === firstTab),
    );
    results.forEach((r) =>
      r.classList.toggle(
        "predictive-search__results--active",
        r.dataset.resultsType === activeType,
      ),
    );
  }

  /**
   * Handles tab click to switch visible results panel.
   * @param {Event} event
   */
  onTabClick(event) {
    const tab = event.target.closest("[data-tab]");
    if (!tab) return;

    const type = tab.dataset.tab;
    const tabs = this.querySelectorAll("[data-tab]");
    const results = this.querySelectorAll("[data-results-type]");

    tabs.forEach((t) =>
      t.classList.toggle("predictive-search__tab--active", t === tab),
    );
    results.forEach((r) =>
      r.classList.toggle(
        "predictive-search__results--active",
        r.dataset.resultsType === type,
      ),
    );
  }

  /**
   * Switches the active option based on direction.
   * @param direction
   */
  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const searchLinks = this.querySelectorAll("[data-predictive-search-link]");

    const visibleElements = this.getVisibleElements(searchLinks);

    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    const selectedElementIndex = this.findSelectedElementIndex(
      visibleElements,
      selectedElement,
    );

    activeElementIndex = this.setActiveElementIndex(
      moveUp,
      selectedElementIndex,
      visibleElements,
    );

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = visibleElements[activeElementIndex];

    this.setActiveElement(activeElement, selectedElement);
  }

  /**
   * Filters and returns only visible elements from a NodeList.
   * @param {NodeList} elements
   * @returns {Array} Array of visible elements.
   */
  getVisibleElements(elements) {
    return Array.from(elements).filter(
      (element) => element.offsetParent !== null,
    );
  }

  /**
   * Finds the index of the selected element within all visible elements.
   * @param {Array} allVisibleElements
   * @param {Element} selectedElement
   * @returns {number}
   */
  findSelectedElementIndex(allVisibleElements, selectedElement) {
    let selectedElementIndex = -1;
    let index = 0;

    while (selectedElementIndex === -1 && index < allVisibleElements.length) {
      if (allVisibleElements[index] === selectedElement)
        selectedElementIndex = index;

      index++;
    }

    return selectedElementIndex;
  }

  /**
   * Calculates the next index in a circular manner.
   * @param {number} currentIndex
   * @param {number} length
   * @returns {number|*}
   */
  getNextIndex(currentIndex, length) {
    return currentIndex === length - 1 ? 0 : currentIndex + 1;
  }

  /**
   * Calculates the previous index in a circular manner.
   * @param {number} currentIndex
   * @param {number} length
   * @returns {number}
   */
  getPreviousIndex(currentIndex, length) {
    return currentIndex === 0 ? length - 1 : currentIndex - 1;
  }

  /**
   * Determines the new active element index based on direction.
   * @param {boolean} moveUp
   * @param {number} selectedElementIndex
   * @param {Array} visibleElements
   * @returns {number|*}
   */
  setActiveElementIndex(moveUp, selectedElementIndex, visibleElements) {
    return moveUp
      ? this.getPreviousIndex(selectedElementIndex, visibleElements.length)
      : this.getNextIndex(selectedElementIndex, visibleElements.length);
  }

  /**
   * Sets the active element and updates ARIA attributes.
   * @param {Element} activeElement
   * @param {Element} selectedElement
   */
  setActiveElement(activeElement, selectedElement) {
    activeElement.setAttribute("aria-selected", "true");

    if (selectedElement) selectedElement.setAttribute("aria-selected", "false");

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  /**
   * Selects the currently highlighted option.
   */
  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"][data-predictive-search-link]',
    );

    if (selectedOption) selectedOption.click();
  }

  /**
   * Sets the live region text for accessibility.
   */
  setLiveRegionText() {
    const countElement = this.querySelector(
      "[data-predictive-search-live-region-count-value]",
    );
    const statusText = countElement?.textContent;

    if (!this.statusElement) return;

    this.statusElement.textContent = statusText;
  }

  /**
   * Opens the predictive search dropdown.
   */
  open() {
    this.input?.setAttribute("aria-expanded", "true");
    this.predictiveSearch.classList.add(this.ACTIVE_CLASS);
  }

  /**
   * Closes the predictive search dropdown.
   * @param {boolean} clearSearchTerm
   */
  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
  }

  /**
   * Closes the results and optionally clears the search term.
   * @param {boolean} clearSearchTerm
   */
  closeResults(clearSearchTerm = false) {
    if (!this.input) return;
    if (clearSearchTerm) this.input.value = "";

    this.predictiveSearch.classList.remove(this.ACTIVE_CLASS);

    this.input.setAttribute("aria-activedescendant", "");
    this.input.setAttribute("aria-expanded", "false");
  }

  /**
   * Reads recently viewed items from session storage and renders them.
   */
  renderRecentlyViewed() {
    const recentlyViewed = this.recentlyViewed;
    if (!recentlyViewed) return;

    const items = this.getRecentlyViewedItems();

    if (!items.length) {
      recentlyViewed.style.display = "none";
      return;
    }

    const list = recentlyViewed.querySelector(".recently-viewed__list");
    const template = this.recentlyViewedItemTemplate;

    if (!list || !template) return;

    list
      .querySelectorAll(".recently-viewed__item")
      .forEach((el) => el.remove());

    items.forEach(({ url, src, alt }) => {
      const clone = template.content.cloneNode(true);
      const link = clone.querySelector(".recently-viewed__link");
      const img = clone.querySelector(".recently-viewed__image");

      if (link) link.href = url;
      if (img) {
        img.src = src;
        img.alt = alt || "";
      }

      list.appendChild(clone);
    });

    recentlyViewed.style.removeProperty("display");

    const clearBtn = recentlyViewed.querySelector(
      ".recently-viewed__clear-button",
    );
    clearBtn?.addEventListener("click", () => this.clearRecentlyViewed());
  }

  /**
   * Returns recently viewed products from session storage.
   * @returns {Array}
   */
  getRecentlyViewedItems() {
    try {
      return JSON.parse(
        sessionStorage.getItem("recently-viewed-products") || "[]",
      );
    } catch {
      return [];
    }
  }

  /**
   * Clears recently viewed products from session storage and hides the section.
   */
  clearRecentlyViewed() {
    sessionStorage.removeItem("recently-viewed-products");

    const recentlyViewed = this.recentlyViewed;
    if (!recentlyViewed) return;

    recentlyViewed
      .querySelector(".recently-viewed__list")
      ?.querySelectorAll(".recently-viewed__item")
      .forEach((el) => el.remove());

    recentlyViewed.style.display = "none";
  }

  /**
   * Returns the trimmed value of the search input.
   * @returns {string}
   */
  get query() {
    return this.input?.value.trim() || "";
  }

  get recentlyViewed() {
    return this.querySelector("[data-recently-viewed]");
  }

  get recentlyViewedItemTemplate() {
    return this.querySelector("[data-recently-viewed-item]");
  }
}

customElements.define("predictive-search", PredictiveSearch);
